import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Loader2} from 'lucide-react';
import {trpc, type RouterOutput, type RouterInput} from '@common-web/lib/trpc';
import {formatDate} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {StatusIndicator} from '@internal/pages/group-pipeline/components/StatusIndicator';
import {TenantTag} from '@internal/pages/group-pipeline/components/TenantTag';
import {VbaStatusIndicator} from '@internal/pages/group-pipeline/components/VbaStatusIndicator';

type BoardData = RouterOutput['internal']['groupPipeline']['board'];
type BoardColumn = BoardData['columns'][number];
type BoardCard = RouterOutput['internal']['groupPipeline']['boardColumn']['cards'][number];
export type BoardFilters = Omit<
  RouterInput['internal']['groupPipeline']['boardColumn'],
  'columnKey' | 'cursor' | 'limit'
>;

const PAGE_SIZE = 15;

// Bottom padding, borders and margins of the containers around the board. Summed from styles
// rather than measured, since the layout stretches to the window and would feed back into it.
function spaceBelow(el: HTMLElement) {
  let total = 0;
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node);
    total +=
      parseFloat(style.paddingBottom) +
      parseFloat(style.borderBottomWidth) +
      parseFloat(style.marginBottom);
  }
  return total;
}

// Sizes the board to the rest of the window so each lane scrolls on its own.
function useFillHeight() {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      setHeight(
        Math.max(360, window.innerHeight - (rect.top + window.scrollY) - spaceBelow(ref.current)),
      );
    };
    measure();
    window.addEventListener('resize', measure);
    // Filter chips can wrap and push the board down.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, []);
  return {ref, height};
}

export function GroupPipelineBoard({
  data,
  filters,
  hideEmptyColumns,
  isLoading,
  isFetching,
}: {
  data: BoardData | undefined;
  filters: BoardFilters;
  hideEmptyColumns: boolean;
  isLoading: boolean;
  isFetching: boolean;
}) {
  const {ref, height} = useFillHeight();

  return (
    <div
      ref={ref}
      style={{height}}
      className={
        'flex gap-3 overflow-x-auto pb-2 ' + (isFetching ? 'opacity-60 transition-opacity' : '')
      }
    >
      {isLoading ? (
        <div className='w-full py-16 text-center'>
          <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : (
        data?.columns
          .filter((column) => !hideEmptyColumns || column.total > 0)
          .map((column) => <BoardLane key={column.key} column={column} filters={filters} />)
      )}
    </div>
  );
}

function BoardLane({column, filters}: {column: BoardColumn; filters: BoardFilters}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const cards = trpc.internal.groupPipeline.boardColumn.useInfiniteQuery(
    {...filters, columnKey: column.key, limit: PAGE_SIZE},
    {
      enabled: column.total > 0,
      initialCursor: 0,
      getNextPageParam: (last) => last.nextCursor,
      placeholderData: (previous) => previous,
    },
  );
  const {hasNextPage, isFetchingNextPage, fetchNextPage} = cards;

  // Load the next page as the bottom of the lane scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      {root: scrollRef.current, rootMargin: '200px'},
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaded = cards.data?.pages.flatMap((page) => page.cards) ?? [];

  return (
    <div className='flex w-72 shrink-0 flex-col gap-1.5'>
      <div className='flex items-center gap-2 rounded-lg bg-[#ECECEC] px-4 py-3'>
        <span className='text-base font-semibold text-gray-900'>{column.label}</span>
        <span className='text-base text-gray-500'>{column.total}</span>
      </div>
      <div
        ref={scrollRef}
        className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg bg-[#ECECEC] p-2'
      >
        {column.total === 0 ? (
          <p className='px-1 py-6 text-center text-xs text-muted-foreground'>
            No groups at this step
          </p>
        ) : cards.isLoading ? (
          <Loader2 className='mx-auto my-6 h-5 w-5 shrink-0 animate-spin text-muted-foreground' />
        ) : (
          <>
            {loaded.map((card) => (
              <PipelineCard key={card.vbaGroupId} card={card} />
            ))}
            <div ref={sentinelRef} className='h-px shrink-0' />
            {isFetchingNextPage && (
              <Loader2 className='mx-auto my-2 h-4 w-4 shrink-0 animate-spin text-muted-foreground' />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PipelineCard({card}: {card: BoardCard}) {
  const location = useLocation();
  return (
    <Link
      to={`/group-pipeline/${card.vbaGroupId}`}
      state={{from: location.pathname + location.search}}
      className='block shrink-0 space-y-2 rounded-xl border border-[#E3E3E3] bg-white p-4 text-sm shadow-sm hover:border-gray-400'
    >
      <div className='flex items-start justify-between gap-2'>
        <span className='font-medium leading-snug'>{card.name}</span>
        <TenantTag shortName={card.tenantName} />
      </div>
      <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'>
        {[`${card.members.toLocaleString()} members`, card.state].filter(Boolean).join(' · ')}
        <VbaStatusIndicator status={card.vbaStatus} quiet />
      </div>
      <StatusIndicator status={card.status} context={card.statusContext} />
      {card.stepLabel && (
        <div className='text-xs'>
          <span className='text-muted-foreground'>Step: </span>
          {card.stepLabel}
        </div>
      )}
      {card.alsoOpen.length > 0 && (
        <div className='text-xs text-muted-foreground'>Also open: {card.alsoOpen.join(', ')}</div>
      )}
      <div className='border-t pt-2 text-xs text-muted-foreground'>
        Effective {formatDate(card.effectiveDate)}
      </div>
    </Link>
  );
}
